<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useBridgeOutboundCall } from 'dashboard/composables/useBridgeOutboundCall';
import { useAccount } from 'dashboard/composables/useAccount';
import { defaultDialCountry } from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper.js';

import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['called']);

const { t } = useI18n();
const { voiceInboxes, isCallDisabled, isInitiatingCall, startCall } =
  useBridgeOutboundCall();
const { currentAccount } = useAccount();

const selectedInboxId = ref(voiceInboxes.value[0]?.id ?? null);
const rawNumber = ref('');
const showError = ref(false);

const inboxOptions = computed(() =>
  voiceInboxes.value.map(inbox => ({
    value: inbox.id,
    label: inbox.phone_number
      ? `${inbox.name} · ${inbox.phone_number}`
      : inbox.name,
  }))
);

const selectedInbox = computed(() =>
  voiceInboxes.value.find(inbox => inbox.id === selectedInboxId.value)
);

// A local number resolves against the inbox's country (or the account's
// approved country), so 0567893412 dials +972567893412; E.164 passes through.
const dialNumber = computed(() => {
  const country = defaultDialCountry(
    selectedInbox.value?.phone_number,
    currentAccount.value
  );
  const parsed = parsePhoneNumberFromString(rawNumber.value.trim(), country);
  return parsed?.isValid() ? parsed.number : '';
});

const onCall = async () => {
  showError.value = !dialNumber.value;
  if (showError.value) return;

  const called = await startCall({
    inbox: selectedInbox.value,
    phone: dialNumber.value,
  });
  if (called) {
    rawNumber.value = '';
    emit('called');
  }
};
</script>

<template>
  <form
    class="w-full md:w-[42rem] flex flex-col gap-4 p-4 bg-n-alpha-3 border border-n-strong shadow-sm backdrop-blur-[100px] rounded-xl"
    @submit.prevent="onCall"
  >
    <Select
      v-model="selectedInboxId"
      :aria-label="t('COMPOSE_NEW_CONVERSATION.DIALER.INBOX_LABEL')"
      :options="inboxOptions"
    />
    <Input
      v-model="rawNumber"
      type="tel"
      autofocus
      :label="t('COMPOSE_NEW_CONVERSATION.DIALER.PHONE_LABEL')"
      :placeholder="t('COMPOSE_NEW_CONVERSATION.DIALER.PHONE_PLACEHOLDER')"
      :message="
        showError ? t('COMPOSE_NEW_CONVERSATION.DIALER.INVALID_PHONE') : ''
      "
      :message-type="showError ? 'error' : 'info'"
      @input="showError = false"
    />
    <div class="flex justify-end">
      <Button
        type="submit"
        icon="i-ri-phone-fill"
        :label="t('COMPOSE_NEW_CONVERSATION.DIALER.CALL')"
        :disabled="isCallDisabled || !selectedInbox"
        :is-loading="isInitiatingCall"
      />
    </div>
  </form>
</template>
